import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import type {
    DataProcessorOfficerAccessResponse,
    DataProcessorOfficerAccessSearchResponse,
    DataProcessorOfficerRequest,
    DataProcessorOfficerUsersSearchResponse,
    DataProcessorOfficerVesselGroupsSearchResponse,
    UpsertDataProcessorOfficerAccessRequest,
} from '@routes/sidebar/admin-center/tenant-client/___pages___/roles/api/crewAndShoreDPO.api.ts';
import {
    createApiDataProcessorOfficerAccess,
    deleteApiDataProcessorOfficerAccess,
    getApiDataProcessorOfficerAccess,
    getApiDataProcessorOfficerUsers,
    getApiDataProcessorOfficerVesselGroups,
    updateApiDataProcessorOfficerAccess,
} from '@routes/sidebar/admin-center/tenant-client/___pages___/roles/api/crewAndShoreDPO.api.ts';
import { extractError } from '@utils/slice.ts';

interface DataProcessorOfficerCrewError {
    message?: string;
    [key: string]: any;
}

interface DataProcessorOfficerCrewState {
    error: DataProcessorOfficerCrewError | null;
    dataProcessorOfficerCrewData: DataProcessorOfficerAccessSearchResponse | null;
    dataProcessorOfficerCrewUsersData: DataProcessorOfficerUsersSearchResponse | null;
    dataProcessorOfficerCrewVesselGroupsData: DataProcessorOfficerVesselGroupsSearchResponse | null;

    isGetDataProcessorOfficerCrewPending: boolean;
    isCreateDataProcessorOfficerCrewPending: boolean;
    isUpdateDataProcessorOfficerCrewPending: boolean;
    isDeleteDataProcessorOfficerCrewPending: boolean;
    isGetDataProcessorOfficerCrewUsersPending: boolean;
    isGetDataProcessorOfficerCrewVesselGroupsPending: boolean;
}

const initialState: DataProcessorOfficerCrewState = {
    error: null,
    dataProcessorOfficerCrewData: null,
    dataProcessorOfficerCrewUsersData: null,
    dataProcessorOfficerCrewVesselGroupsData: null,

    isGetDataProcessorOfficerCrewPending: false,
    isCreateDataProcessorOfficerCrewPending: false,
    isUpdateDataProcessorOfficerCrewPending: false,
    isDeleteDataProcessorOfficerCrewPending: false,
    isGetDataProcessorOfficerCrewUsersPending: false,
    isGetDataProcessorOfficerCrewVesselGroupsPending: false,
};

export const getDataProcessorOfficerCrew = createAsyncThunk<
    DataProcessorOfficerAccessSearchResponse,
    { tenantAlias: string; payload: DataProcessorOfficerRequest },
    { rejectValue: DataProcessorOfficerCrewError }
>('dataProcessorOfficerCrew/getDataProcessorOfficerCrew', ({ tenantAlias, payload }, { rejectWithValue }) =>
    getApiDataProcessorOfficerAccess(tenantAlias, payload)
        .then((res) => res)
        .catch((err) => rejectWithValue(err)),
);

export const createDataProcessorOfficerCrew = createAsyncThunk<
    DataProcessorOfficerAccessResponse,
    { tenantAlias: string; payload: UpsertDataProcessorOfficerAccessRequest },
    { rejectValue: DataProcessorOfficerCrewError }
>('dataProcessorOfficerCrew/createDataProcessorOfficerCrew', ({ tenantAlias, payload }, { rejectWithValue }) =>
    createApiDataProcessorOfficerAccess(tenantAlias, payload)
        .then((res) => res)
        .catch((err) => rejectWithValue(err)),
);

export const updateDataProcessorOfficerCrew = createAsyncThunk<
    DataProcessorOfficerAccessResponse,
    { tenantAlias: string; accessId: number; payload: UpsertDataProcessorOfficerAccessRequest },
    { rejectValue: DataProcessorOfficerCrewError }
>(
    'dataProcessorOfficerCrew/updateDataProcessorOfficerCrew',
    ({ tenantAlias, accessId, payload }, { rejectWithValue }) =>
        updateApiDataProcessorOfficerAccess(tenantAlias, accessId, payload)
            .then((res) => res)
            .catch((err) => rejectWithValue(err)),
);

export const deleteDataProcessorOfficerCrew = createAsyncThunk<
    void,
    { tenantAlias: string; accessId: number },
    { rejectValue: DataProcessorOfficerCrewError }
>('dataProcessorOfficerCrew/deleteDataProcessorOfficerCrew', ({ tenantAlias, accessId }, { rejectWithValue }) =>
    deleteApiDataProcessorOfficerAccess(tenantAlias, accessId)
        .then(() => undefined)
        .catch((err) => rejectWithValue(err)),
);

export const getDataProcessorOfficerCrewUsers = createAsyncThunk<
    DataProcessorOfficerUsersSearchResponse,
    { tenantAlias: string; payload: DataProcessorOfficerRequest },
    { rejectValue: DataProcessorOfficerCrewError }
>('dataProcessorOfficerCrew/getDataProcessorOfficerCrewUsers', ({ tenantAlias, payload }, { rejectWithValue }) =>
    getApiDataProcessorOfficerUsers(tenantAlias, payload)
        .then((res) => res)
        .catch((err) => rejectWithValue(err)),
);

export const getDataProcessorOfficerCrewVesselGroups = createAsyncThunk<
    DataProcessorOfficerVesselGroupsSearchResponse,
    { tenantAlias: string; payload: DataProcessorOfficerRequest },
    { rejectValue: DataProcessorOfficerCrewError }
>('dataProcessorOfficerCrew/getDataProcessorOfficerCrewVesselGroups', ({ tenantAlias, payload }, { rejectWithValue }) =>
    getApiDataProcessorOfficerVesselGroups(tenantAlias, payload)
        .then((res) => res)
        .catch((err) => rejectWithValue(err)),
);

const dataProcessorOfficerCrewSlice = createSlice({
    name: 'dataProcessorOfficerCrew',
    initialState,
    reducers: {},
    extraReducers: (builder) => {
        builder.addCase(getDataProcessorOfficerCrew.pending, (state) => {
            state.isGetDataProcessorOfficerCrewPending = true;
            state.error = null;
        });
        builder.addCase(getDataProcessorOfficerCrew.fulfilled, (state, action) => {
            state.isGetDataProcessorOfficerCrewPending = false;
            state.dataProcessorOfficerCrewData = action.payload;
        });
        builder.addCase(getDataProcessorOfficerCrew.rejected, (state, action) => {
            state.isGetDataProcessorOfficerCrewPending = false;
            state.error = extractError(action);
        });

        builder.addCase(createDataProcessorOfficerCrew.pending, (state) => {
            state.isCreateDataProcessorOfficerCrewPending = true;
            state.error = null;
        });
        builder.addCase(createDataProcessorOfficerCrew.fulfilled, (state) => {
            state.isCreateDataProcessorOfficerCrewPending = false;
        });
        builder.addCase(createDataProcessorOfficerCrew.rejected, (state, action) => {
            state.isCreateDataProcessorOfficerCrewPending = false;
            state.error = extractError(action);
        });

        builder.addCase(updateDataProcessorOfficerCrew.pending, (state) => {
            state.isUpdateDataProcessorOfficerCrewPending = true;
            state.error = null;
        });
        builder.addCase(updateDataProcessorOfficerCrew.fulfilled, (state) => {
            state.isUpdateDataProcessorOfficerCrewPending = false;
        });
        builder.addCase(updateDataProcessorOfficerCrew.rejected, (state, action) => {
            state.isUpdateDataProcessorOfficerCrewPending = false;
            state.error = extractError(action);
        });

        builder.addCase(deleteDataProcessorOfficerCrew.pending, (state) => {
            state.isDeleteDataProcessorOfficerCrewPending = true;
            state.error = null;
        });
        builder.addCase(deleteDataProcessorOfficerCrew.fulfilled, (state) => {
            state.isDeleteDataProcessorOfficerCrewPending = false;
        });
        builder.addCase(deleteDataProcessorOfficerCrew.rejected, (state, action) => {
            state.isDeleteDataProcessorOfficerCrewPending = false;
            state.error = extractError(action);
        });

        builder.addCase(getDataProcessorOfficerCrewUsers.pending, (state) => {
            state.isGetDataProcessorOfficerCrewUsersPending = true;
            state.error = null;
        });
        builder.addCase(getDataProcessorOfficerCrewUsers.fulfilled, (state, action) => {
            state.isGetDataProcessorOfficerCrewUsersPending = false;
            state.dataProcessorOfficerCrewUsersData = action.payload;
        });
        builder.addCase(getDataProcessorOfficerCrewUsers.rejected, (state, action) => {
            state.isGetDataProcessorOfficerCrewUsersPending = false;
            state.error = extractError(action);
        });

        builder.addCase(getDataProcessorOfficerCrewVesselGroups.pending, (state) => {
            state.isGetDataProcessorOfficerCrewVesselGroupsPending = true;
            state.error = null;
        });
        builder.addCase(getDataProcessorOfficerCrewVesselGroups.fulfilled, (state, action) => {
            state.isGetDataProcessorOfficerCrewVesselGroupsPending = false;
            state.dataProcessorOfficerCrewVesselGroupsData = action.payload;
        });
        builder.addCase(getDataProcessorOfficerCrewVesselGroups.rejected, (state, action) => {
            state.isGetDataProcessorOfficerCrewVesselGroupsPending = false;
            state.error = extractError(action);
        });
    },
});

export default dataProcessorOfficerCrewSlice.reducer;
